import { useEffect, useRef, useState } from 'react'
import type mapboxgl from 'mapbox-gl'
import { Search, Home, Building2, MapPin, Crosshair, X, Lock, Globe } from 'lucide-react'
import {
  geocodeAddress,
  reverseGeocode,
  forwardGeocodeSuggestions,
  retrieveGeocodeSuggestion,
  MAPBOX_TOKEN,
} from '../lib/geocode'
import type { GeocodeSuggestion } from '../lib/geocode'
import { reportMapWebglFallback } from '../lib/mapWebgl'

export type LocationMode = 'private' | 'venue' | 'pin'

interface Props {
  addressText: string
  onAddressChange: (v: string) => void
  lat: number | null
  lng: number | null
  onCoordinatesChange: (lat: number, lng: number) => void
  addressError?: string
  showModeToggle?: boolean
  mode?: LocationMode
  onModeChange?: (mode: LocationMode) => void
  onHideAddressChange?: (hide: boolean) => void
  locationName?: string
  onLocationNameChange?: (name: string) => void
  placeholder?: string
  defaultLat?: number | null
  defaultLng?: number | null
}

interface SelectedPlace {
  name: string
  address: string
}

const DEFAULT_CENTER: [number, number] = [-117.2614, 34.0483]
const MARKER_COLOR = '#8b6914'

const MODE_OPTIONS: { value: LocationMode; Icon: React.ElementType; label: string; shortLabel: string }[] = [
  { value: 'private', Icon: Home,      label: 'Private home', shortLabel: 'Private' },
  { value: 'venue',   Icon: Building2, label: 'Public venue', shortLabel: 'Venue'   },
  { value: 'pin',     Icon: MapPin,    label: 'Drop pin',     shortLabel: 'Pin'     },
]

const MODE_NOTES: Record<LocationMode, { Icon: React.ElementType; text: string; blue: boolean }> = {
  private: { Icon: Lock,   text: 'Your exact location stays private — guests see only the general area until you approve their RSVP.', blue: true  },
  venue:   { Icon: Globe,  text: 'The full address is shown publicly on the event.',                          blue: false },
  pin:     { Icon: MapPin, text: 'The dropped location is shown publicly on the event.',                      blue: false },
}

export default function LocationPicker({
  addressText,
  onAddressChange,
  lat,
  lng,
  onCoordinatesChange,
  addressError,
  showModeToggle,
  mode = 'private',
  onModeChange,
  onHideAddressChange,
  locationName,
  onLocationNameChange,
  placeholder = 'Search for a place or address…',
  defaultLat,
  defaultLng,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef       = useRef<mapboxgl.Map | null>(null)
  const markerRef    = useRef<mapboxgl.Marker | null>(null)
  const hasToken     = Boolean(MAPBOX_TOKEN)
  // Runtime WebGL init failure → show a small placeholder in place of the map.
  const [webglFailed, setWebglFailed] = useState(false)

  const [pinPlaced,      setPinPlaced]      = useState(lat !== null)
  const [selectedPlace,  setSelectedPlace]  = useState<SelectedPlace | null>(
    addressText ? { name: locationName?.trim() || addressText, address: addressText } : null,
  )
  const [query,          setQuery]          = useState('')
  const [suggestions,    setSuggestions]    = useState<GeocodeSuggestion[]>([])
  const [showSuggestions,setShowSuggestions]= useState(false)
  const [resolvingId,    setResolvingId]    = useState<string | null>(null)
  const [geocoding,      setGeocoding]      = useState(false)
  const [geocodeError,   setGeocodeError]   = useState<string | null>(null)
  const [locating,       setLocating]       = useState(false)

  const debounceRef      = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef       = useRef<HTMLDivElement>(null)
  const sessionTokenRef  = useRef(crypto.randomUUID())
  const modeRef          = useRef(mode)
  const onCoordsRef      = useRef(onCoordinatesChange)
  const onAddressRef     = useRef(onAddressChange)
  const latRef           = useRef(lat)
  const lngRef           = useRef(lng)

  useEffect(() => { modeRef.current    = mode              }, [mode])
  useEffect(() => { onCoordsRef.current  = onCoordinatesChange }, [onCoordinatesChange])
  useEffect(() => { onAddressRef.current = onAddressChange     }, [onAddressChange])
  useEffect(() => { latRef.current       = lat                 }, [lat])
  useEffect(() => { lngRef.current       = lng                 }, [lng])

  // ─── Map init ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current || !hasToken || webglFailed) return
    let cancelled = false

    // Any throw here (most commonly `Failed to initialize WebGL` from `new mb.Map`)
    // must be caught, not left as an unhandled rejection — show the placeholder.
    const handleInitError = (err: unknown) => {
      if (cancelled) return
      reportMapWebglFallback(err)
      setWebglFailed(true)
    }

    void import('mapbox-gl').then((mod) => {
      if (cancelled || !containerRef.current) return
      try {
      const mb = mod.default
      mb.accessToken = MAPBOX_TOKEN as string

      const initLat = latRef.current
      const initLng = lngRef.current
      const fallback: [number, number] = (defaultLat != null && defaultLng != null)
        ? [defaultLng, defaultLat] : DEFAULT_CENTER
      const center: [number, number] = initLat !== null && initLng !== null
        ? [initLng, initLat] : fallback

      const map = new mb.Map({
        container: containerRef.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center,
        zoom: initLat !== null ? 13 : 11,
        attributionControl: false,
      })

      map.on('load', () => {
        if (cancelled) return
        mapRef.current = map
        map.resize()

        const finalLat = latRef.current
        const finalLng = lngRef.current

        if (finalLat !== null || modeRef.current !== 'pin') {
          const pos: [number, number] = finalLat !== null && finalLng !== null
            ? [finalLng, finalLat] : center
          const marker = new mb.Marker({ draggable: true, color: MARKER_COLOR })
            .setLngLat(pos).addTo(map)
          markerRef.current = marker
          marker.on('dragend', () => {
            const p = marker.getLngLat()
            onCoordsRef.current(p.lat, p.lng)
            setPinPlaced(true)
            void reverseGeocode(p.lat, p.lng).then((addr) => {
              if (addr) onAddressRef.current(addr)
            })
          })
        }

        map.on('click', (e) => {
          if (modeRef.current !== 'pin' && latRef.current !== null) return
          const { lat: clickLat, lng: clickLng } = e.lngLat
          if (!markerRef.current) {
            const marker = new mb.Marker({ draggable: true, color: MARKER_COLOR })
              .setLngLat([clickLng, clickLat]).addTo(map)
            markerRef.current = marker
            marker.on('dragend', () => {
              const p = marker.getLngLat()
              onCoordsRef.current(p.lat, p.lng)
              setPinPlaced(true)
              void reverseGeocode(p.lat, p.lng).then((addr) => {
                if (addr) onAddressRef.current(addr)
              })
            })
          } else {
            markerRef.current.setLngLat([clickLng, clickLat])
          }
          onCoordsRef.current(clickLat, clickLng)
          setPinPlaced(true)
          void reverseGeocode(clickLat, clickLng).then((addr) => {
            if (addr) onAddressRef.current(addr)
          })
        })

        if (modeRef.current === 'pin') map.getCanvas().style.cursor = 'crosshair'
      })
      } catch (err) {
        handleInitError(err)
      }
    }).catch(handleInitError)

    return () => {
      cancelled = true
      markerRef.current = null
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [hasToken, webglFailed]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const canvas = mapRef.current?.getCanvas()
    if (!canvas) return
    canvas.style.cursor = mode === 'pin' ? 'crosshair' : ''
  }, [mode])

  // Move marker when coords change externally
  useEffect(() => {
    if (lat === null || lng === null) return
    const marker = markerRef.current
    const map    = mapRef.current
    if (!marker || !map) return
    marker.setLngLat([lng, lat])
    map.flyTo({ center: [lng, lat], zoom: 14, duration: 500 })
    setPinPlaced(true)
  }, [lat, lng])

  // ─── Autocomplete ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (mode === 'pin' || query.length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      void forwardGeocodeSuggestions(query, sessionTokenRef.current).then((results) => {
        setSuggestions(results)
        setShowSuggestions(results.length > 0)
      })
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query, mode])

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [])

  // ─── Handlers ─────────────────────────────────────────────────────────────

  function handleModeChange(newMode: LocationMode) {
    if (newMode === 'pin') {
      setSelectedPlace(null)
      setQuery('')
      setSuggestions([])
    }
    onModeChange?.(newMode)
    onHideAddressChange?.(newMode === 'private')
  }

  async function handleSuggestionSelect(s: GeocodeSuggestion) {
    setShowSuggestions(false)
    setResolvingId(s.mapboxId)
    const resolved = await retrieveGeocodeSuggestion(s.mapboxId, sessionTokenRef.current)
    sessionTokenRef.current = crypto.randomUUID()
    setResolvingId(null)
    if (!resolved) {
      setGeocodeError('Could not resolve location. Try typing the full address.')
      return
    }
    const fullAddress = resolved.fullAddress || s.address || s.placeName
    setSelectedPlace({ name: s.placeName, address: fullAddress })
    setQuery('')
    setSuggestions([])
    setGeocodeError(null)
    onAddressChange(fullAddress)
    onLocationNameChange?.(s.placeName)
    onCoordinatesChange(resolved.lat, resolved.lng)
  }

  async function handleEnterFallback() {
    if (!query.trim()) return
    setGeocoding(true)
    setGeocodeError(null)
    const coords = await geocodeAddress(query.trim())
    setGeocoding(false)
    if (!coords) {
      setGeocodeError('Could not locate that address. Try adding more detail, like city and region.')
      return
    }
    setSelectedPlace({ name: query.trim(), address: query.trim() })
    setQuery('')
    onAddressChange(query.trim())
    onCoordinatesChange(coords.lat, coords.lng)
  }

  function handleClearSelection() {
    setSelectedPlace(null)
    setQuery('')
    setGeocodeError(null)
    onAddressChange('')
    onLocationNameChange?.('')
    onCoordinatesChange(0, 0)
    setPinPlaced(false)
  }

  function handleLocate() {
    if (!navigator.geolocation) return
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false)
        const { latitude: gLat, longitude: gLng } = pos.coords
        mapRef.current?.flyTo({ center: [gLng, gLat], zoom: 14, duration: 600 })
        if (mode === 'pin') {
          const map = mapRef.current
          if (map && !markerRef.current) {
            void import('mapbox-gl').then((mod) => {
              const mb = mod.default
              const marker = new mb.Marker({ draggable: true, color: MARKER_COLOR })
                .setLngLat([gLng, gLat]).addTo(map)
              markerRef.current = marker
              marker.on('dragend', () => {
                const p = marker.getLngLat()
                onCoordsRef.current(p.lat, p.lng)
                setPinPlaced(true)
                void reverseGeocode(p.lat, p.lng).then((addr) => {
                  if (addr) onAddressRef.current(addr)
                })
              })
            })
          } else {
            markerRef.current?.setLngLat([gLng, gLat])
          }
          onCoordinatesChange(gLat, gLng)
          setPinPlaced(true)
          void reverseGeocode(gLat, gLng).then((addr) => {
            if (addr) onAddressChange(addr)
          })
        }
      },
      () => setLocating(false),
    )
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  const note = mode ? MODE_NOTES[mode] : null

  return (
    <div className="flex flex-col gap-3">

      {/* Mode toggle */}
      {showModeToggle && (
        <div className="flex rounded-[10px] overflow-hidden border border-line-soft">
          {MODE_OPTIONS.map(({ value, Icon, label, shortLabel }) => {
            const active = mode === value
            return (
              <button
                key={value}
                type="button"
                onClick={() => handleModeChange(value)}
                className={`
                  flex-1 flex items-center justify-center gap-1.5 py-2 text-[12.5px] font-medium
                  cursor-pointer border-0 border-r border-line-soft font-sans last:border-r-0
                  transition-all duration-[120ms]
                  ${active ? 'bg-ink text-surface' : 'bg-s2 text-muted hover:text-ink-2'}
                `}
              >
                <Icon size={13} />
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{shortLabel}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Search / selected display (hidden in pin mode) */}
      {mode !== 'pin' && (
        <div ref={wrapperRef} className="relative">
          {selectedPlace ? (
            /* Selected place — two-line display */
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-surface"
              style={{ borderColor: addressError ? 'var(--warn)' : 'var(--line)' }}
            >
              <MapPin size={15} className="shrink-0 text-muted" />
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-ink truncate">{selectedPlace.name}</div>
                {selectedPlace.address && selectedPlace.address !== selectedPlace.name && (
                  <div className="text-[12px] text-muted truncate">{selectedPlace.address}</div>
                )}
              </div>
              <button
                type="button"
                onClick={handleClearSelection}
                className="shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-transparent border-0 cursor-pointer text-muted hover:text-ink hover:bg-s2"
                title="Change location"
              >
                <X size={13} />
              </button>
            </div>
          ) : (
            /* Search input */
            <div className="relative flex items-center">
              <Search size={15} className="absolute left-3 text-muted pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setGeocodeError(null); setShowSuggestions(true) }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (suggestions.length > 0) void handleSuggestionSelect(suggestions[0])
                    else void handleEnterFallback()
                  }
                  if (e.key === 'Escape') setShowSuggestions(false)
                }}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true) }}
                placeholder={placeholder}
                aria-invalid={Boolean(addressError)}
                aria-autocomplete="list"
                className="w-full h-10 pl-9 pr-9 box-border rounded-lg text-base md:text-[13.5px] font-sans bg-surface text-ink outline-none"
                style={{ border: `1px solid ${addressError ? 'var(--warn)' : 'var(--line)'}` }}
              />
              {(query || geocoding) && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); setSuggestions([]); setShowSuggestions(false) }}
                  className="absolute right-3 flex items-center justify-center bg-transparent border-0 cursor-pointer text-muted hover:text-ink"
                >
                  {geocoding ? (
                    <span className="text-[11px] text-muted">…</span>
                  ) : (
                    <X size={13} />
                  )}
                </button>
              )}
            </div>
          )}

          {/* Autocomplete dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              className="absolute top-full left-0 right-0 z-[100] mt-1 rounded-[10px] bg-surface border border-line-soft overflow-hidden"
              style={{ boxShadow: '0 8px 24px -8px rgba(0,0,0,0.18)' }}
            >
              {suggestions.map((s, i) => (
                <button
                  key={s.mapboxId}
                  type="button"
                  disabled={resolvingId !== null}
                  onMouseDown={(e) => { e.preventDefault(); void handleSuggestionSelect(s) }}
                  className={`
                    block w-full px-3.5 py-2.5 text-left bg-transparent border-0 cursor-pointer
                    font-sans leading-[1.3] hover:bg-s2 disabled:opacity-60
                    ${i < suggestions.length - 1 ? 'border-b border-line-soft' : ''}
                    ${resolvingId === s.mapboxId ? 'bg-s2' : ''}
                  `}
                >
                  <div className="text-[13.5px] text-ink">{s.placeName}</div>
                  {s.address && <div className="text-[11.5px] text-muted mt-[1px]">{s.address}</div>}
                </button>
              ))}
            </div>
          )}

          {(addressError || geocodeError) && (
            <div className="text-[12px] text-warn mt-1">{addressError ?? geocodeError}</div>
          )}
        </div>
      )}

      {/* Privacy / info note */}
      {showModeToggle && note && (
        <div className={`flex items-start gap-2 px-3 py-2.5 rounded-[10px] text-[12.5px] leading-[1.5] ${note.blue ? 'bg-primary-soft text-primary-deep' : 'bg-s2 text-ink-2'}`}>
          <note.Icon size={13} className="shrink-0 mt-[1px]" />
          <span>{note.text}</span>
        </div>
      )}

      {/* Map */}
      {hasToken && !webglFailed ? (
        <>
          <div className="relative">
            <div
              ref={containerRef}
              className="h-[200px] rounded-xl overflow-hidden border border-line-soft"
            />

            {/* Center / locate button — map overlay, top-right */}
            <button
              type="button"
              onClick={handleLocate}
              disabled={locating}
              title="Center map on my location"
              className="absolute top-2 right-2 z-10 flex items-center justify-center w-8 h-8 rounded-lg bg-surface border border-line-soft shadow-sm cursor-pointer disabled:opacity-50 hover:bg-s2"
            >
              <Crosshair size={15} className="text-ink-2" />
            </button>

            {/* Click-to-drop hint for pin mode */}
            {mode === 'pin' && !pinPlaced && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-black/55 text-white rounded-lg px-3.5 py-2 text-[13px] font-medium">
                  Click the map to drop a pin
                </div>
              </div>
            )}
          </div>

          {pinPlaced && lat !== null && (
            <div className="text-[11px] text-muted text-right -mt-1">
              Drag the pin to fine-tune · {lat.toFixed(5)}, {lng?.toFixed(5)}
            </div>
          )}
        </>
      ) : (
        <div
          className="h-[120px] rounded-xl border border-line-soft flex items-center justify-center text-[12px] text-muted"
          style={{ background: 'repeating-linear-gradient(45deg, var(--line-soft) 0 6px, var(--surface-2) 6px 14px)' }}
        >
          {webglFailed ? 'Map unavailable in this browser' : 'Map unavailable — Mapbox token not configured'}
        </div>
      )}
    </div>
  )
}
